import { UserEntity, stringifyEntityRef } from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useAsync } from 'react-use';

/** The annotation the msgraph provider keys a user by in Entra. */
const ANNOTATION_USER_ID = 'graph.microsoft.com/user-id';

/**
 * One member of an Entra group, under whichever identifiers the CMDB knows
 * them by - at least one of the three is set. See `CmdbGroupMember` in the
 * catalog module for why a member is a map rather than a single account.
 */
export type CmdbGroupMember = {
    email?: string | null;
    upn?: string | null;
    object_id?: string | null;
};

export type EntraGroupIn = {
    name: string;
    full_name?: string | null;
    sid?: string | null;
    members: CmdbGroupMember[];
    /**
     * The legacy `CAuth_…` group of a technical team group - see `CmdbGroup` in
     * the catalog module.
     */
    cauth_full_name?: string | null;
    /**
     * The members the CMDB file declares, a subset of `members`. They differ on
     * the viewer group, whose remaining members are derived from the contacts
     * of the team or department - see `EntraGroup` in the catalog module.
     *
     * Missing on an entity built before the provider stated it, which is not
     * the same as a group that declares nobody: reading the one as the other
     * makes every member look derived. Hence optional rather than defaulted,
     * see `EntraGroupOut`.
     */
    declaredMembers?: CmdbGroupMember[];
};

export type EntraGroupsIn = {
    technical?: EntraGroupIn[];
    custom?: EntraGroupIn[];
};

export type EntraGroupOut = Omit<
    EntraGroupIn,
    'members' | 'declaredMembers'
> & {
    members: UserEntity[];
    /**
     * `undefined` where the entity states no `declaredMembers` at all - the
     * absence is passed on rather than smoothed into an empty list, so that a
     * card can tell "nobody was written down" from "this entity cannot say".
     * The latter is nothing to edit against: everyone would read as derived,
     * and a write built from that would take the whole group out of the file.
     */
    declaredMembers?: UserEntity[];
};

export type EntraGroupsOut = {
    technical: EntraGroupOut[];
    custom: EntraGroupOut[];
};

function displayNameOf(user: UserEntity): string {
    return user.spec.profile?.displayName ?? user.metadata.name;
}

/**
 * The catalog users behind the members of the Entra groups of a team or of a
 * department.
 *
 * A member is looked up under whichever identifier the CMDB states for them:
 * a written-down member carries an `@netrtl.com` account, while one derived
 * from a contact carries a plain mail address and nothing else. A member no
 * user matches is left out rather than shown as a bare address - the cards
 * below are rosters of people, and half a person is worse than none.
 */
export function useUsersFromGroupMembers(groups?: EntraGroupsIn): {
    entities: EntraGroupsOut | undefined;
    loading: boolean;
} {
    const catalogApi = useApi(catalogApiRef);
    // The object is rebuilt on every render - it is read off `entity.spec` -
    // so its content is what the fetch is keyed on, not its identity.
    const groupsKey = JSON.stringify(groups ?? null);

    const { loading, value } = useAsync(async () => {
        if (!groups) {
            return undefined;
        }
        const all = [...(groups.technical ?? []), ...(groups.custom ?? [])];
        const members = all.flatMap(group => group.members);

        // One filter per member: the catalog ORs the list, and each entry is a
        // single identifier rather than all of them at once, which the catalog
        // would AND together.
        const filters = members.flatMap(member => {
            const of = (key: string, value?: string | null) =>
                value ? [{ kind: 'User', [key]: value }] : [];
            return [
                ...of('spec.profile.userPrincipalName', member.upn),
                ...of('spec.profile.email', member.email),
                ...of(
                    `metadata.annotations.${ANNOTATION_USER_ID}`,
                    member.object_id,
                ),
            ];
        });

        // Chunked: a filter list of a few hundred entries makes for a URL
        // the catalog rejects.
        const chunkSize = 10;
        const responses = await Promise.all(
            Array.from(
                { length: Math.ceil(filters.length / chunkSize) },
                (_, i) =>
                    catalogApi.getEntities({
                        filter: filters.slice(
                            i * chunkSize,
                            (i + 1) * chunkSize,
                        ),
                    }),
            ),
        );

        const byUpn = new Map<string, UserEntity>();
        const byEmail = new Map<string, UserEntity>();
        const byObjectId = new Map<string, UserEntity>();
        for (const user of responses.flatMap(it => it.items) as UserEntity[]) {
            const profile = user.spec.profile as any;
            if (profile?.userPrincipalName) {
                byUpn.set(
                    profile.userPrincipalName.toLocaleLowerCase('en-US'),
                    user,
                );
            }
            if (profile?.email) {
                byEmail.set(profile.email.toLocaleLowerCase('en-US'), user);
            }
            const objectId = user.metadata.annotations?.[ANNOTATION_USER_ID];
            if (objectId) {
                byObjectId.set(objectId, user);
            }
        }

        const resolve = (member: CmdbGroupMember) =>
            (member.upn
                ? byUpn.get(member.upn.toLocaleLowerCase('en-US'))
                : undefined) ??
            (member.email
                ? byEmail.get(member.email.toLocaleLowerCase('en-US'))
                : undefined) ??
            (member.object_id ? byObjectId.get(member.object_id) : undefined);

        // One user per person: a contact the CMDB also lists as a declared
        // member of the viewer group is in it twice - once by account, once
        // by mail address - and both resolve to the same user, which would
        // show as two cards and count twice.
        const resolveAll = (of: CmdbGroupMember[]) =>
            [
                ...new Map(
                    of
                        .map(resolve)
                        .filter((user): user is UserEntity => !!user)
                        .map(user => [stringifyEntityRef(user), user]),
                ).values(),
            ].sort((a, b) => displayNameOf(a).localeCompare(displayNameOf(b)));

        const out = (group: EntraGroupIn): EntraGroupOut => ({
            ...group,
            members: resolveAll(group.members),
            declaredMembers: group.declaredMembers
                ? resolveAll(group.declaredMembers)
                : undefined,
        });

        return {
            technical: (groups.technical ?? []).map(out),
            custom: (groups.custom ?? []).map(out),
        };
    }, [catalogApi, groupsKey]);

    return { loading, entities: value };
}
